/**
 * Pure helpers for DeleteJobFamilyLevelDialog (ER-11643, Job Family §10.4).
 *
 * Kept framework-free so mode resolution, sibling filtering, copy templates and
 * server-error mapping can be unit tested without mounting the SFC (mirrors the
 * approach used by the sibling JobFamilyLevelsTable row helpers).
 */

/** Renders the "{ordinal} · {name}" label shared by the select trigger and menu items. */
export function formatOrdinalName(ordinal, name) {
  return `${ordinal} \u00B7 ${name}`;
}

/**
 * Resolves which of the three Situation groups the dialog is in:
 * - "onlyLevel"    — no other Levels in the Family to reassign to (blocked).
 * - "noPositions"  — Levels exist to reassign to, but this Level has no Positions.
 * - "reassign"     — Levels exist and this Level has Positions: Needs reassignment /
 *                     Picker open / Target selected are sub-states of this mode.
 */
export function resolveMode(level, siblingLevels) {
  const siblings = Array.isArray(siblingLevels) ? siblingLevels : [];
  if (siblings.length === 0) {
    return "onlyLevel";
  }
  const positionCount = Number(level?.positionCount) || 0;
  return positionCount > 0 ? "reassign" : "noPositions";
}

/**
 * The other Levels in the same Family, excluding the Level being deleted BY ID —
 * Level names are not unique within a Family, so name matching would remove the
 * wrong row (Job Family §9).
 */
export function buildSiblingOptions(level, siblingLevels) {
  const siblings = Array.isArray(siblingLevels) ? siblingLevels : [];
  return siblings.filter((sibling) => sibling?.id !== level?.id);
}

export function reassignSupportingText(level) {
  const n = Number(level?.positionCount) || 0;
  return (
    `${n} positions are assigned to ${formatOrdinalName(level?.ordinal, level?.name)}. ` +
    `Choose the level those positions should move to. This can\u2019t be undone.`
  );
}

export function noPositionsSupportingText(level) {
  return (
    `No positions are assigned to ${formatOrdinalName(level?.ordinal, level?.name)}, so ` +
    `nothing needs to be reassigned. The remaining levels are renumbered automatically. ` +
    `This can\u2019t be undone.`
  );
}

export function onlyLevelSupportingText(level) {
  const n = Number(level?.positionCount) || 0;
  return (
    `${formatOrdinalName(level?.ordinal, level?.name)} is the only level in this Job Family. ` +
    `A Job Family must always have at least one level, and there is nowhere to move its ` +
    `${n} positions. Add another level first, or retire the Job Family instead.`
  );
}

export function pickerExclusionCaption(level) {
  return (
    `Only other levels in this Job Family are listed. ` +
    `${formatOrdinalName(level?.ordinal, level?.name)} is excluded.`
  );
}

export function targetSelectedHelper(level, target) {
  const n = Number(level?.positionCount) || 0;
  return `All ${n} positions will move to ${formatOrdinalName(target?.ordinal, target?.name)}.`;
}

/** Server error codes this dialog must surface rather than fail silently (§10.4). */
export const ERROR_MESSAGES = {
  JOB_FAMILY_LEVEL_CANNOT_DELETE_LAST:
    "This is the only level in the Job Family and can't be deleted.",
  JOB_FAMILY_LEVEL_REASSIGN_TARGET_REQUIRED:
    "Choose a level to move these positions to before deleting.",
  JOB_FAMILY_LEVEL_REASSIGN_TARGET_INVALID:
    "That level is no longer available. Choose another level to move these positions to.",
};

/** Maps a known server error code to its friendly copy; unknown codes pass through as-is. */
export function resolveErrorMessage(error) {
  const code = String(error || "").trim();
  if (!code) {
    return "";
  }
  return ERROR_MESSAGES[code] || code;
}
